import { BenchmarkingDashboardDetails } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardDetails";
import { BenchmarkingProviderFlyoutData } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingProviderFlyoutData";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { CompetitiveInsightsManageAddModalOptionsModel } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsManageAddModalOptionsModel";
import { SupersetProjectConfigurationRequestModel } from "@app/overviews/network-benchmarking-dashboard/manage/shared/SupersetProjectConfigurationRequestModel";
import { MedicaidContractPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidContractPrimaryFiltersPayload";
import { MedicaidFilterData } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidFilterData";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import { MedicaidNetworkFilterData } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetworkFilterData";
import { MedicaidPrimaryFilterOptionUpdates } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidPrimaryFilterOptionUpdates";
import { MedicaidPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidPrimaryFiltersPayload";
import { EffectivenessAndEfficiencyData, HospitalAndPcpCountData, MarketPenetrationData, ProvidersByNetworkAndVolumeData, ProvidersByNetworkData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { AffiliationsData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/AffiliationsData";
import { ProviderOverlapData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/ProviderOverlapData";
import { authenticatedDownloadAsync } from "@app/shared/AuthenticatedDownload";
import ServiceBase from "@app/shared/serviceBase";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";

/** handles communication across the wire when interacting with the network benchmarking dashboard */
export default class BenchmarkingService extends ServiceBase {
  private benchmarkingEndpoint = `${this.apiEndpoint}/benchmarkingmedicaid`;
  public readonly lineOfBusiness = BenchmarkingLineOfBusinessEnum.Medicaid;

  public async getBenchmarkingPrimaryFilterData(signal?: AbortSignal): Promise<MedicaidFilterData> {
    return await this.makeGet<MedicaidFilterData>(`primary-filters`, null, this.benchmarkingEndpoint, false, undefined, signal);
  }

  public async getBenchmarkingNetworkData(primaryFilters: MedicaidPrimaryFiltersPayload): Promise<MedicaidNetworkFilterData> {
    if (!primaryFilters) {
      return null;
    }

    return await this.makePost<MedicaidNetworkFilterData>(`get-networks`, primaryFilters, this.benchmarkingEndpoint);
  }

  public async getPrimaryFilterOptionUpdates(stateId: number, program: string, signal?: AbortSignal): Promise<MedicaidPrimaryFilterOptionUpdates> {
    return await this.makeGet<MedicaidPrimaryFilterOptionUpdates>(
      `get-primary-filter-option-updates?stateId=${stateId}&program=${encodeURIComponent(program)}`,
      null,
      this.benchmarkingEndpoint,
      false,
      undefined,
      signal
    );
  }

  public async getAddConfigurationOptions(clientId: number, signal?: AbortSignal): Promise<CompetitiveInsightsManageAddModalOptionsModel> {
    return await this.makeGet<CompetitiveInsightsManageAddModalOptionsModel>(`get-add-configuration-options?clientId=${clientId}`, null, this.benchmarkingEndpoint, false, undefined, signal);
  }

  public async addSupersetProjectConfiguration(model: SupersetProjectConfigurationRequestModel): Promise<void> {
    return await this.makePost<void>(`add-superset-project-config`, model, this.benchmarkingEndpoint);
  }

  public async editSupersetProjectConfiguration(model: SupersetProjectConfigurationRequestModel): Promise<void> {
    return await this.makePut<void>(`edit-superset-project-config`, model, this.benchmarkingEndpoint);
  }

  public async getContractDetails(primaryFilters: MedicaidContractPrimaryFiltersPayload, signal?: AbortSignal): Promise<MedicaidNetwork> {
    if (!primaryFilters) {
      return null;
    }

    return await this.makePost<MedicaidNetwork>(`contract`, primaryFilters, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getHospitalAndPcpCountData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<HospitalAndPcpCountData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<HospitalAndPcpCountData[]>(`hospital-and-pcp-count`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProvidersByNetworkData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProvidersByNetworkData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<ProvidersByNetworkData[]>(`providers-by-network`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getMarketPenetrationData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<MarketPenetrationData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<MarketPenetrationData[]>(`market-penetration`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProvidersByNetworkAndVolumeData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProvidersByNetworkAndVolumeData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<ProvidersByNetworkAndVolumeData[]>(`providers-by-network-and-volume`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getEffectivenessAndEfficiencyData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<EffectivenessAndEfficiencyData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<EffectivenessAndEfficiencyData[]>(`effectiveness-and-efficiency`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProviderOverlapData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProviderOverlapData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<ProviderOverlapData>(`provider-overlap`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getAffiliationsData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<AffiliationsData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<AffiliationsData[]>(`affiliations`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getDashboardDetailsData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<BenchmarkingDashboardDetails[]> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingDashboardDetails[]>(`dashboard-details`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProviderFlyoutData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<BenchmarkingProviderFlyoutData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingProviderFlyoutData>(`provider-details`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getDataCoverageCsv(fileName?: string): Promise<void> {
    if (!!fileName && fileName.split('.').pop().toLowerCase() !== `csv`) {
      fileName += `.csv`;
    }

    return await authenticatedDownloadAsync(`${this.benchmarkingEndpoint}/data-coverage/report/csv`, null, fileName, new URLSearchParams());
  }
}
